import { keepPreviousData, queryOptions } from '@tanstack/react-query';

import type { Guid } from '@apple/utils/primitive';

import { getOrCreateCart } from '../api';
import { getProductSelections } from '../api/products';
import { getItems, getItemsCount } from '../api/quantities';
import { getStoreSelections } from '../api/stores';
import type { StoreCartLineFilters } from '../models/items';

export const cartQueryKeys = {
	all: ['cart'],
	cartId: ['cart', 'id'],
	items: (cartId: Guid) => ['cart', cartId, 'items'],
	selectedStores: (cartId: Guid) => ['cart', cartId, 'selections', 'store'],
	selectedProducts: (cartId: Guid) => ['cart', cartId, 'selections', 'product'],
	selectedQuantities: (cartId: Guid, request?: StoreCartLineFilters) =>
		request ? [...cartQueryKeys.items(cartId), request] : cartQueryKeys.items(cartId),
	selectedQuantitiesCount: (cartId: Guid) => ['cart', cartId, 'quantities', 'count'],
} as const;

export const cartQueryOptions = {
	cartId: (enabled: boolean) =>
		queryOptions({
			enabled,
			queryKey: cartQueryKeys.cartId,
			queryFn: ({ signal }) => getOrCreateCart(signal),
		}),
	selectedStores: ({ cartId, enabled }: { cartId: Guid; enabled: boolean }) =>
		queryOptions({
			enabled,
			queryKey: cartQueryKeys.selectedStores(cartId),
			queryFn: ({ signal }) => getStoreSelections(signal),
			placeholderData: keepPreviousData,
			initialDataUpdatedAt: 0,
			initialData: [],
		}),
	selectedProducts: ({ cartId, enabled }: { cartId: Guid; enabled: boolean }) =>
		queryOptions({
			enabled,
			queryKey: cartQueryKeys.selectedProducts(cartId),
			queryFn: ({ signal }) => getProductSelections(signal),
			placeholderData: keepPreviousData,
			initialDataUpdatedAt: 0,
			initialData: {
				productIds: [],
				quantities: {},
			},
		}),
	selectedQuantities: ({
		cartId,
		request,
		enabled,
	}: {
		cartId: Guid;
		request?: StoreCartLineFilters;
		enabled: boolean;
	}) =>
		queryOptions({
			enabled,
			queryKey: cartQueryKeys.selectedQuantities(cartId, request),
			queryFn: async ({ signal }) => getItems(request, signal),
			placeholderData: keepPreviousData,
		}),
	selectedQuantitiesCount: ({ cartId, enabled }: { cartId: Guid; enabled: boolean }) =>
		queryOptions({
			enabled,
			queryKey: cartQueryKeys.selectedQuantitiesCount(cartId),
			queryFn: ({ signal }) => getItemsCount(signal),
			placeholderData: keepPreviousData,
			initialDataUpdatedAt: 0,
			initialData: 0,
		}),
} as const;
